import { isEmpty, isEqual } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FormState, UseFormReset, UseFormWatch } from 'react-hook-form';

import { useQuestionFormsOrchestrator } from '@/data/context/QuestionFormsOrchestrator';
import { Question } from '@/models/Question';

type BaseFields = Record<string, unknown>;

type UseFormRegistrationProps<T extends BaseFields> = {
  identifier: string;
  submit: (values: T) => Promise<Question>;
  validate: () => Promise<boolean>;
  watch: UseFormWatch<T>;
  defaultValues: T;
  reset: UseFormReset<T>;
  formState: FormState<T>;
};

export const useFormRegistration = <T extends BaseFields>({
  identifier,
  submit,
  validate,
  reset,
  watch,
  formState,
  defaultValues,
}: UseFormRegistrationProps<T>) => {
  const { registerForm, unregisterForm, updateFormEntry, resetFailed } =
    useQuestionFormsOrchestrator();
  const [hasChanges, setHasChanges] = useState(false);

  const {
    submit: submitWrapper,
    validate: validateWrapper,
    values: valuesRef,
  } = useStableFormValues({
    submit,
    validate,
    defaultValues,
  });

  const handleReset = () => {
    setHasChanges(false);
    updateFormEntry(identifier, { hasChanges: false });
    resetFailed(identifier);
    reset(defaultValues);
  };

  useEffect(() => {
    reset(defaultValues, { keepDirtyValues: true });
  }, [defaultValues, reset]);

  useEffect(() => {
    const subscription = watch(() => {
      if (!isEmpty(formState?.errors)) validate();
    });

    return () => subscription.unsubscribe();
  }, [formState, validate, watch]);

  useEffect(() => {
    registerForm(identifier, {
      submit: submitWrapper,
      validate: validateWrapper,
      hasChanges: false,
    });
    return () => unregisterForm(identifier);
  }, [
    identifier,
    registerForm,
    unregisterForm,
    submitWrapper,
    validateWrapper,
  ]);

  useEffect(() => {
    const subscription = watch(value => {
      valuesRef.current = value as T;
      const changed = !isEqual(value, defaultValues);
      setHasChanges(changed);
      updateFormEntry(identifier, { hasChanges: changed });
    });
    return () => {
      setHasChanges(false);
      updateFormEntry(identifier, { hasChanges: false });
      subscription.unsubscribe();
    };
  }, [defaultValues, watch, identifier, updateFormEntry, valuesRef]);

  return { hasChanges, handleReset };
};

const useStableFormValues = <T extends BaseFields>({
  submit,
  validate,
  defaultValues,
}: Pick<
  UseFormRegistrationProps<T>,
  'submit' | 'validate' | 'defaultValues'
>) => {
  const valuesRef = useRef<T>(defaultValues);
  const validateRef = useRef(validate);
  validateRef.current = validate;
  const submitRef = useRef(submit);
  submitRef.current = submit;

  const submitWrapper = useCallback(
    () => submitRef.current(valuesRef.current),
    [],
  );

  const validateWrapper = useCallback(() => validateRef.current(), []);

  return {
    submit: submitWrapper,
    validate: validateWrapper,
    values: valuesRef,
  };
};
